import { Injectable, OnDestroy, signal } from "@angular/core";
import { Media, ImageType, SyncableStatus } from "../../../core/database/db";
import { BlobService } from "../../../shared/services/blob.service";
import { CompressionService } from "../../../shared/services/compression.service";
import { LoggerService } from "../../../shared/services/logger.service";
import { v4 as uuidv4 } from "uuid";

export type MediaForCompression = Media & { file: File };

/**
 * Per-component service that manages the
 * encode → show-immediately → compress-in-background pipeline used wherever
 * the app lets users pick images.
 *
 * Lifecycle:
 *  1. `await encode(files, parentIdentifier)` — converts files to base64 Media
 *     records and sets `isLoadingImages` while doing so.
 *  2. Add the returned records to whatever signal the component uses for display.
 *  3. `scheduleCompression(records, onCompressed)` — queues background compression;
 *     calls `onCompressed(compressedRecords)` when the batch finishes. Multiple calls
 *     are automatically serialised so concurrent uploads never interleave.
 *  4. `await waitForCompletion()` before any submit that needs compressed data
 *     (e.g. to ensure the latest base64/size is in the DB before emitting).
 */
@Injectable({
  providedIn: "root"
})
export class ImageCompressionPipelineService implements OnDestroy {
  readonly isLoadingImages = signal(false);
  readonly isCompressingImages = signal(false);
  readonly numberOfImagesBeingProcessed = signal(0);

  private compressionQueue = Promise.resolve();
  private pendingCompressions = 0;

  constructor(
    private blobService: BlobService,
    private compressionService: CompressionService,
    private logger: LoggerService
  ) { }

  /** Encodes each file to base64 and returns a `MediaForCompression` record per file. */
  async encode(files: File[], parentIdentifier: string, imageType: ImageType = ImageType.Issue): Promise<MediaForCompression[]> {
    this.isLoadingImages.set(true);
    this.numberOfImagesBeingProcessed.set(files.length);

    const records = await Promise.all(files.map(async file => {
      const mediaRecord: MediaForCompression = {
        name: file.name,
        base64Image: await this.blobService.blobToBase64(file, this.logger),
        mimeType: file.type,
        fileIdentifier: uuidv4(),
        size: file.size,
        uploaded: 0,
        readyForUpload: 0,
        updatedAt: new Date(),
        parentIdentifier,
        syncStatus: SyncableStatus.Pending,
        deleted: 0,
        imageType,
        file
      };
      return mediaRecord;
    }));

    this.isLoadingImages.set(false);
    return records;
  }

  /**
   * Queues background compression for `records`. Calls `onCompressed` with the
   * updated (smaller) Media records when this batch is done. Multiple batches
   * are serialised — the second batch won't start until the first finishes.
   */
  compress(
    records: MediaForCompression[],
    onCompressed: (compressedMediaRecords: MediaForCompression[]) => Promise<void>
  ): void {
    this.pendingCompressions++;
    this.isCompressingImages.set(true);

    this.compressionQueue = this.compressionQueue.then(async () => {
      this.logger.info(`Starting compression of ${records.length} images`);

      const compressedFiles = await Promise.all(
        records.map(record =>
          this.compressionService.compressImage({ file: record.file, identifier: record.fileIdentifier })
        )
      );

      this.logger.info(`Compression finished for ${compressedFiles.length} images`);

      const compressedRecords: MediaForCompression[] = await Promise.all(
        records.map(async record => {
          const compressed = compressedFiles.find(c => c.identifier === record.fileIdentifier);
          const file = compressed?.file ?? record.file;
          const compressedMedia: MediaForCompression = {
            ...record,
            base64Image: await this.blobService.blobToBase64(file, this.logger),
            size: file.size,
            mimeType: file.type,
            file
          };
          return compressedMedia;
        })
      );

      await onCompressed(compressedRecords);

      this.pendingCompressions--;
      if (this.pendingCompressions === 0) {
        this.isCompressingImages.set(false);
        this.numberOfImagesBeingProcessed.set(0);
      }
    });
  }

  /** Resolves once all queued compression batches have finished. */
  waitForCompletion(): Promise<void> {
    return this.compressionQueue;
  }

  ngOnDestroy(): void {
    this.compressionQueue = Promise.resolve();
    this.pendingCompressions = 0;
  }
}
